import { Options } from 'ky';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import { getPrefixUrl, ModuleNames } from 'utils/apiClient/url';
import {
  Theme,
  Settings,
  HeroImage,
  StoreLogo,
  HeroVideo,
  ThemeUpdate,
  DeleteMedia,
} from './types';

const cdClientOpts: Options = {
  prefixUrl: getPrefixUrl(ModuleNames.CUSTOMER_DISPLAY),
};

/* eslint-disable import/prefer-default-export */
export const useCustomerDisplayApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const customerDisplayApiClient = authenticatedApiClient.extend(cdClientOpts);
  return {
    fetchThemes: () =>
      customerDisplayApiClient.get('v1/display/themes/').json<Theme[]>(),
    fetchSettings: () =>
      customerDisplayApiClient.get('v1/settings/').json<Settings>(),
    updateThemeSettings: (settings: {
      values: Partial<Settings & ThemeUpdate>;
    }) => {
      const payload = settings.values?.id
        ? { theme: settings.values }
        : settings.values;

      return customerDisplayApiClient
        .patch('v1/settings/', {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .json<Settings>();
    },

    addLogo: (payload: StoreLogo) => {
      const formData = new FormData();
      formData.append(payload.image_type, payload.file);
      return customerDisplayApiClient
        .post('v1/display/logo/', { body: formData })
        .json<Settings>();
    },
    addHeroImage: (payload: HeroImage) => {
      const formData = new FormData();
      formData.append(payload.image_type, payload.file);
      return customerDisplayApiClient
        .post('v1/display/hero_image/', { body: formData })
        .json<Settings>();
    },
    addHeroVideo: (payload: HeroVideo) => {
      const formData = new FormData();
      formData.append(payload.video_type, payload.file);
      return customerDisplayApiClient
        .post('v1/display/hero_video/', {
          body: formData,
        })
        .json<Settings>();
    },
    deleteMedia: (payload: DeleteMedia) => {
      const formData = new FormData();
      const {
        delete_hero_image = false,
        delete_hero_video = false,
        delete_logo = false,
        hero_image_url = null,
        hero_video_url = null,
        logo_url,
      } = payload;
      formData.append('delete_hero_image', delete_hero_image.toString());
      formData.append('delete_hero_video', delete_hero_video.toString());
      formData.append('delete_logo', delete_logo.toString());
      formData.append('hero_image_url', hero_image_url ?? '');
      formData.append('hero_video_url', hero_video_url ?? '');
      formData.append('logo_url', logo_url ?? '');
      return customerDisplayApiClient
        .post('v1/display/delete_media_files/', {
          body: formData,
        })
        .json<Settings>();
    },
  };
};
